import { BrandLogo } from "@/theme/brand-logo";
import { paths } from "@/config/paths";
import { Linkedin, ArrowRight, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getUiBranding, getFooterNewsletterLink } from "@/theme/ui-branding";
import { openConsentPreferences } from "@/lib/consent";
import TiktokIcon from "@/icons/tiktok";
import FacebookIcon from "@/icons/facebook";
import InstagramIcon from "@/icons/instagram";
import GithubIcon from "@/icons/github";
import RedditIcon from "@/icons/reddit";
import XIcon from "@/icons/x";

const rrssIcons = {
  linkedin: Linkedin,
  x: XIcon,
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  github: GithubIcon,
  tiktok: TiktokIcon,
  reddit: RedditIcon,
};

interface CatalogFooterProps {
  statusIndicator?: React.ReactNode;
}

export function CatalogFooter({ statusIndicator }: CatalogFooterProps) {
  const uiBranding = getUiBranding();
  const newsletterLink = getFooterNewsletterLink();
  const rrss = Object.entries(uiBranding.social || {})
    .filter(([key]) => key in rrssIcons)
    .map(([key, value]) => {
      const Icon = rrssIcons[key as keyof typeof rrssIcons];
      return (
        <a key={key} href={value} target="_blank" rel="noopener noreferrer" className="hover:text-info">
          <Icon className="w-5 h-5" />
        </a>
      );
    });
  return (
    <footer className="bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-20">
          <div>
            <div className="flex items-center gap-2 mb-6">
              <BrandLogo variant="horizontal" />
            </div>
            {rrss.length > 0 && <div className="flex items-center gap-4">{rrss}</div>}
            {newsletterLink && (
              <div className={rrss.length > 0 ? "mt-8 pt-8 border-t border-slate-200 dark:border-slate-800" : "mt-2"}>
                {newsletterLink.message && (
                  <p className="mb-4 text-sm leading-relaxed text-slate-700 dark:text-slate-300 max-w-sm">
                    {newsletterLink.message}
                  </p>
                )}
                <Button asChild variant="default" size="lg" className="w-full sm:w-auto shadow-md">
                  <a
                    href={newsletterLink.href}
                    target={newsletterLink.external ? "_blank" : "_self"}
                    rel="noopener noreferrer"
                  >
                    <Mail className="w-4 h-4" aria-hidden />
                    {newsletterLink.label}
                    <ArrowRight className="w-4 h-4" aria-hidden />
                  </a>
                </Button>
              </div>
            )}
          </div>
          <div>
            <h5 className="font-bold text-sm mb-6 text-slate-900 dark:text-white">Website</h5>
            <ul className="space-y-4 text-sm text-slate-800 dark:text-slate-400">
              <li>
                {uiBranding.links?.home && (
                  <a
                    href={uiBranding.links?.home}
                    target="_self"
                    rel="noopener noreferrer"
                    className="hover:text-info focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    Home
                  </a>
                )}
              </li>
              <li>
                {uiBranding.links?.blog && (
                  <a
                    href={uiBranding.links?.blog}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-info focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    Blog
                  </a>
                )}
              </li>
              <li>
                <a
                  href={paths.support.getHref()}
                  className="hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  Contact
                </a>
              </li>
              <li>
                {uiBranding.links?.status && (
                  <a
                    href={uiBranding.links?.status}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-info focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    Status Page
                  </a>
                )}
              </li>
            </ul>
          </div>
          <div>
            <h5 className="font-bold text-sm mb-6 text-slate-900 dark:text-white flex flex-col">
              {uiBranding.productName} Platform
              {uiBranding.productName !== "viaNexus" && (
                <span>
                  Powered by{" "}
                  <a
                    href="https://vianexus.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-info inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    viaNexus
                  </a>
                </span>
              )}
            </h5>
            <ul className="space-y-4 text-sm text-slate-800 dark:text-slate-400">
              <li>
                <a
                  href="/catalog/#pricing"
                  className="hover:text-primary inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  Data Catalog & Pricing
                  <ArrowRight className="w-4 h-4 shrink-0" />
                </a>
              </li>
              <li>
                <a
                  href="/docs"
                  className="hover:text-primary inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  API Docs
                  <ArrowRight className="w-4 h-4 shrink-0" />
                </a>
              </li>
              <li>
                <a
                  href={paths.auth.signIn.getHref()}
                  className="hover:text-primary inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  Sign in
                  <ArrowRight className="w-4 h-4 shrink-0" />
                </a>
              </li>
              <li>
                <a
                  href={paths.auth.signUp.getHref()}
                  className="hover:text-primary inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  Create Account
                  <ArrowRight className="w-4 h-4 shrink-0" />
                </a>
              </li>
              {uiBranding.links?.terms && (
                <li>
                  <a
                    href={uiBranding.links?.terms}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-info inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    Terms & Conditions
                    <ArrowRight className="w-4 h-4 shrink-0" />
                  </a>
                </li>
              )}
              {uiBranding.links?.privacy && (
                <li>
                  <a
                    href={uiBranding.links?.privacy}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-info inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    Privacy Policy
                    <ArrowRight className="w-4 h-4 shrink-0" />
                  </a>
                </li>
              )}
              {uiBranding.links?.cookie && (
                <li>
                  <a
                    href={uiBranding.links?.cookie}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-info inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    Cookie Policy
                    <ArrowRight className="w-4 h-4 shrink-0" />
                  </a>
                </li>
              )}
              {/* Unconditional: consent always has a provider, so there is always a panel to reopen. */}
              <li>
                <button
                  type="button"
                  onClick={openConsentPreferences}
                  className="hover:text-info inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  Cookie Settings
                  <ArrowRight className="w-4 h-4 shrink-0" />
                </button>
              </li>
              <li>
                <a
                  href="https://vianexus.com/about"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-info inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  About viaNexus
                  <ArrowRight className="w-4 h-4 shrink-0" />
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4 text-xs font-medium">
          <p>
            &copy; {new Date().getFullYear()} {uiBranding.companyName}. All rights reserved.
          </p>
          {statusIndicator != null ? <div className="flex items-center gap-6">{statusIndicator}</div> : null}
        </div>
      </div>
    </footer>
  );
}
